import { useState, useEffect, useCallback, useMemo } from 'react';
import { startOfWeek, endOfWeek, format, addDays } from 'date-fns';

export const useWeeklyMenu = (auth) => {
    const [currentWeekStart, setCurrentWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [weeklyData, setWeeklyData] = useState({}); // { [dateString]: { date, meals: {}, totals: {} } }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userGoals, setUserGoals] = useState({ kilocalories: 2000, protein: 150, carbs: 200, fat: 60 });

    // Helper to format date consistent with keys
    const getDateKey = (date) => format(date, 'yyyy-MM-dd');

    const getIngredientQuantityInGrams = (quantity, unit, ingredient) => {
        const numericQuantity = Number(quantity || 0);
        if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) return 0;

        if ((unit || ingredient?.standardUnit || 'gramos') === 'gramos') {
            return numericQuantity;
        }

        const gramsPerUnit = Number(ingredient?.gramsPerUnit);
        return Number.isFinite(gramsPerUnit) && gramsPerUnit > 0
            ? numericQuantity * gramsPerUnit
            : 0;
    };

    // Fetch User Goals (Generic, could be moved if needed)
    // Fetch User Goals
    useEffect(() => {
        const fetchUserGoals = async () => {
            if (!auth.authToken) return;
            try {
                // 1. Get User ID
                const userRes = await fetch(`${import.meta.env.VITE_API_URL}/user`, {
                    headers: { Authorization: `Bearer ${auth.authToken}` }
                });
                if (!userRes.ok) return;
                const userData = await userRes.json();
                const userId = userData.id;

                if (!userId) return;

                // 2. Get User Info with Macros
                const infoRes = await fetch(`${import.meta.env.VITE_API_URL}/userinfo/user/${userId}`, {
                    headers: { Authorization: `Bearer ${auth.authToken}` }
                });

                if (infoRes.ok) {
                    const infoData = await infoRes.json();
                    if (infoData.macros) {
                        setUserGoals(infoData.macros);
                    }
                }
            } catch (e) {
                console.error("Error fetching user goals", e);
            }
        };
        fetchUserGoals();
    }, [auth.authToken]);

    // Fetch Weekly Data
    const fetchWeekData = useCallback(async () => {
        if (!auth.authToken) return;
        setLoading(true);
        setError(null);

        const startStr = format(currentWeekStart, 'yyyy-MM-dd');
        const endStr = format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');

        try {
            const [menuRes, unplannedRes] = await Promise.all([
                fetch(`${import.meta.env.VITE_API_URL}/menuschema?start=${startStr}&end=${endStr}`, {
                    headers: { Authorization: `Bearer ${auth.authToken}` }
                }),
                fetch(`${import.meta.env.VITE_API_URL}/unplanned-meals?start=${startStr}&end=${endStr}`, {
                    headers: { Authorization: `Bearer ${auth.authToken}` }
                })
            ]);

            if (!menuRes.ok) throw new Error("Failed to fetch menu");

            const menuData = await menuRes.json();
            const unplannedDataWrapper = await unplannedRes.json();
            const unplannedData = unplannedDataWrapper.data || {};

            // Normalize Data & Collect IDs for batch fetching
            const normalized = {};
            const days = [0, 1, 2, 3, 4, 5, 6].map(offset => addDays(currentWeekStart, offset));

            // Sets to collect unique IDs
            const recipeIds = new Set();
            const ingredientIds = new Set();

            days.forEach(day => {
                const key = getDateKey(day);
                normalized[key] = {
                    date: day,
                    meals: {
                        "Desayuno": { items: [], macros: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 } },
                        "Media mañana": { items: [], macros: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 } },
                        "Comida": { items: [], macros: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 } },
                        "Media tarde": { items: [], macros: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 } },
                        "Cena": { items: [], macros: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 } },
                    },
                    totals: { kilocalories: 0, protein: 0, carbs: 0, fat: 0 }
                };
            });

            // 1. Pass: Collect IDs
            menuData.forEach(item => {
                if (item.recipes && Array.isArray(item.recipes)) {
                    item.recipes.forEach(r => {
                        // r might be an object (populated) or string (id)
                        const id = typeof r === 'object' ? r._id : r;
                        if (id) recipeIds.add(id);
                    });
                }
                if (item.ingredients && Array.isArray(item.ingredients)) {
                    item.ingredients.forEach(i => {
                        const ingId = i.ingredient && typeof i.ingredient === 'object' ? i.ingredient._id : i.ingredient;
                        if (ingId) ingredientIds.add(ingId);
                    });
                }
                if (item.stockUses && Array.isArray(item.stockUses)) {
                    item.stockUses.forEach(s => {
                        const recipeId = s.recipe && typeof s.recipe === 'object' ? s.recipe._id : s.recipe;
                        const stockRecipeId = s.stockItem && typeof s.stockItem === 'object'
                            ? (typeof s.stockItem.sourceRecipe === 'object' ? s.stockItem.sourceRecipe?._id : s.stockItem.sourceRecipe)
                            : null;
                        if (recipeId) recipeIds.add(recipeId);
                        if (stockRecipeId) recipeIds.add(stockRecipeId);
                    });
                }
                if (item.stockPreparations && Array.isArray(item.stockPreparations)) {
                    item.stockPreparations.forEach(s => {
                        const recipeId = s.recipe && typeof s.recipe === 'object' ? s.recipe._id : s.recipe;
                        if (recipeId) recipeIds.add(recipeId);
                    });
                }
            });

            // 2. Batch Fetch Details
            const [recipesDetails, ingredientsDetails] = await Promise.all([
                recipeIds.size > 0
                    ? fetch(`${import.meta.env.VITE_API_URL}/recipes/by-ids?ids=${Array.from(recipeIds).join(',')}`, {
                        headers: { Authorization: `Bearer ${auth.authToken}` }
                    }).then(r => r.json()).catch(() => [])
                    : [],
                ingredientIds.size > 0
                    ? fetch(`${import.meta.env.VITE_API_URL}/ingredients/by-ids?ids=${Array.from(ingredientIds).join(',')}`, {
                        headers: { Authorization: `Bearer ${auth.authToken}` }
                    }).then(r => r.json()).catch(() => [])
                    : []
            ]);

            // Create Lookups
            const recipeMap = new Map((recipesDetails || []).map(r => [r._id, r]));
            const ingredientMap = new Map((ingredientsDetails || []).map(i => [i._id, i]));

            // 3. Populate
            menuData.forEach(item => {
                // Use the raw date string (YYYY-MM-DD) to avoid timezone shifts
                const key = item.date.split('T')[0];

                if (normalized[key] && normalized[key].meals[item.meal]) {
                    const mealEntry = normalized[key].meals[item.meal];
                    mealEntry.rawId = item._id;
                    mealEntry.macros = item.macronutrients || mealEntry.macros;

                    // Helper to calculate total for item if needed, but usually item header has macros.
                    // The item.macronutrients are total for the meal.

                    const stockPrepByRecipeId = new Map();
                    (item.stockPreparations || []).forEach(prep => {
                        const recipeId = prep.recipe && typeof prep.recipe === 'object' ? prep.recipe._id : prep.recipe;
                        if (!recipeId) return;
                        const key = recipeId.toString();
                        const existing = stockPrepByRecipeId.get(key) || { portions: 0 };
                        stockPrepByRecipeId.set(key, {
                            ...existing,
                            ...prep,
                            portions: existing.portions + Number(prep.portions || 0),
                        });
                    });

                    const stockRecipeIds = new Set((item.stockUses || []).map(s => {
                        const recipeId = s.recipe && typeof s.recipe === 'object' ? s.recipe._id : s.recipe;
                        const stockRecipeId = s.stockItem && typeof s.stockItem === 'object'
                            ? (typeof s.stockItem.sourceRecipe === 'object' ? s.stockItem.sourceRecipe?._id : s.stockItem.sourceRecipe)
                            : null;
                        return recipeId || stockRecipeId;
                    }).filter(Boolean).map(id => id.toString()));

                    const recipes = (item.recipes || []).filter(r => {
                        const id = typeof r === 'object' ? r._id : r;
                        return !stockRecipeIds.has(id?.toString());
                    }).map(r => {
                        const id = typeof r === 'object' ? r._id : r;
                        const details = recipeMap.get(id) || {};
                        const macroSource = details.totalMacronutrients || details.nutritionalInfo || details.macronutrients || details.macros || details;
                        const stockPrep = stockPrepByRecipeId.get(id?.toString());
                        return {
                            ...details, // Spread details (name, mainImage, etc.)
                            _id: id,
                            isRecipe: true,
                            quantity: 1,
                            unit: 'ración',
                            name: details.name || "Receta no encontrada",
                            type: 'planned-recipe',
                            kilocalories: macroSource.kilocalories || macroSource.calories || 0,
                            protein: macroSource.protein || macroSource.proteins || 0,
                            carbs: macroSource.carbs || macroSource.carbohydrates || 0,
                            fat: macroSource.fat || macroSource.fats || 0,
                            stockPrepPortions: stockPrep?.portions || 0,
                            stockPreparation: stockPrep,
                        };
                    });

                    const stockItems = (item.stockUses || []).map(s => {
                        const stock = typeof s.stockItem === 'object' ? s.stockItem : {};
                        const recipeId = s.recipe && typeof s.recipe === 'object' ? s.recipe._id : s.recipe;
                        const stockRecipeId = stock && typeof stock.sourceRecipe === 'object' ? stock.sourceRecipe?._id : stock.sourceRecipe;
                        const sourceRecipeId = recipeId || stockRecipeId;
                        const recipeDetails = sourceRecipeId ? (recipeMap.get(sourceRecipeId) || {}) : {};
                        const stockMacros = stock.macrosPerPortion || {};
                        const hasStockMacros = Object.values(stockMacros).some(v => Number(v) > 0);
                        const macroSource = hasStockMacros ? stockMacros : (recipeDetails.totalMacronutrients || {});
                        const portions = Number(s.portions || 1);
                        return {
                            _id: stock._id || s.stockItem,
                            name: stock.name || recipeDetails.name || 'Fondo',
                            mainImage: recipeDetails.mainImage,
                            isRecipe: !!sourceRecipeId,
                            sourceRecipeId,
                            sourceRecipeName: recipeDetails.name,
                            quantity: portions,
                            unit: stock.portionUnit || 'raciones',
                            storage: stock.storage || 'freezer',
                            type: 'planned-stock',
                            stockPortions: portions,
                            portionsAvailable: stock.portionsAvailable || 0,
                            kilocalories: (macroSource.kilocalories || 0) * portions,
                            protein: (macroSource.protein || 0) * portions,
                            carbs: (macroSource.carbs || 0) * portions,
                            fat: (macroSource.fat || 0) * portions,
                        };
                    });

                    const ingredients = (item.ingredients || []).map(i => {
                        const id = i.ingredient && typeof i.ingredient === 'object' ? i.ingredient._id : i.ingredient;
                        const details = ingredientMap.get(id) || {};
                        const unit = i.unit || details.standardUnit || 'gramos';
                        const quantityInGrams = getIngredientQuantityInGrams(i.quantity, unit, details);
                        return {
                            ...details, // Spread details (name, standardUnit, etc)
                            _id: id,
                            quantity: i.quantity,
                            unit,
                            isRecipe: false,
                            name: details.name || "Ingrediente no encontrado",
                            // Calculate specific macros for this quantity
                            kilocalories: (details.kilocalories || 0) * (quantityInGrams / 100),
                            protein: (details.protein || 0) * (quantityInGrams / 100),
                            carbs: (details.carbs || 0) * (quantityInGrams / 100),
                            fat: (details.fat || 0) * (quantityInGrams / 100),
                            type: 'planned-ingredient'
                        };
                    });

                    mealEntry.items = [...stockItems, ...recipes, ...ingredients];
                    normalized[key].meals[item.meal] = mealEntry;
                }
            });

            // 4. Merge Unplanned Meals
            // unplannedData structure: { "YYYY-MM-DD": [ { meal: "Desayuno", ... }, ... ] }
            Object.entries(unplannedData).forEach(([dateKey, mealsList]) => {
                if (normalized[dateKey] && Array.isArray(mealsList)) {
                    mealsList.forEach(uMeal => {
                        const targetMeal = normalized[dateKey].meals[uMeal.meal];
                        if (targetMeal) {
                            // Normalize Unplanned Meal to match FoodItemRow
                            // Include ALL macro fields and AI detection props
                            const normalizedItem = {
                                ...uMeal,
                                _id: uMeal._id,
                                name: uMeal.aiAnalysis?.recipeName || uMeal.aiAnalysis?.dishName || uMeal.description || "Comida externa",
                                mainImage: uMeal.aiAnalysis?.imageUrl,
                                quantity: 1,
                                unit: 'ración',
                                // Include all macros at top level for FoodItemRow
                                kilocalories: uMeal.finalMacronutrients?.kilocalories || 0,
                                protein: uMeal.finalMacronutrients?.protein || 0,
                                carbs: uMeal.finalMacronutrients?.carbs || 0,
                                fat: uMeal.finalMacronutrients?.fat || 0,
                                // Also include structured macros for FoodItemRow detection
                                finalMacronutrients: uMeal.finalMacronutrients,
                                estimatedMacronutrients: uMeal.estimatedMacronutrients,
                                // AI analysis data for badge and image
                                aiAnalysis: uMeal.aiAnalysis,
                                // Flags for FoodItemRow to detect AI meals
                                isUnplanned: true,
                                type: 'unplanned',
                                isRecipe: true // Visually treat as recipe
                            };

                            // Add macros to meal totals
                            targetMeal.macros.kilocalories += uMeal.finalMacronutrients?.kilocalories || 0;
                            targetMeal.macros.protein += uMeal.finalMacronutrients?.protein || 0;
                            targetMeal.macros.carbs += uMeal.finalMacronutrients?.carbs || 0;
                            targetMeal.macros.fat += uMeal.finalMacronutrients?.fat || 0;

                            targetMeal.items.push(normalizedItem);
                        }
                    });
                }
            });

            // Calculate Totals per day
            Object.values(normalized).forEach(day => {
                let dKcal = 0, dProt = 0, dCarbs = 0, dFat = 0;
                Object.values(day.meals).forEach(m => {
                    dKcal += m.macros.kilocalories || 0;
                    dProt += m.macros.protein || 0;
                    dCarbs += m.macros.carbs || 0;
                    dFat += m.macros.fat || 0;
                });
                day.totals = { kilocalories: dKcal, protein: dProt, carbs: dCarbs, fat: dFat };
            });

            setWeeklyData(normalized);

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [auth.authToken, currentWeekStart]);

    useEffect(() => {
        fetchWeekData();
    }, [fetchWeekData]);


    // Derived Global Totals (Memoized)
    const weeklyTotals = useMemo(() => {
        let tKcal = 0, tProt = 0, tCarbs = 0, tFat = 0;
        Object.values(weeklyData).forEach(day => {
            tKcal += day.totals.kilocalories;
            tProt += day.totals.protein;
            tCarbs += day.totals.carbs;
            tFat += day.totals.fat;
        });
        return { kilocalories: tKcal, protein: tProt, carbs: tCarbs, fat: tFat };
    }, [weeklyData]);

    // Navigation Actions
    const nextWeek = () => setCurrentWeekStart(d => addDays(d, 7));
    const prevWeek = () => setCurrentWeekStart(d => addDays(d, -7));
    const goToToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

    // --- Actions ---

    // Generic helper to update or create menu
    const updateMenuBackend = async (existingMenuId, payload, dateStr, mealName) => {
        const url = existingMenuId
            ? `${import.meta.env.VITE_API_URL}/menuschema/${existingMenuId}`
            : `${import.meta.env.VITE_API_URL}/menuschema`;

        const method = existingMenuId ? 'PUT' : 'POST';

        // If creating new, we need date and meal name
        const finalPayload = existingMenuId ? payload : { ...payload, date: dateStr, meal: mealName };

        const res = await fetch(url, {
            method,
            headers: {
                'Authorization': `Bearer ${auth.authToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(finalPayload)
        });

        if (!res.ok) throw new Error("Failed to update menu");
        return await res.json();
    };

    const addFoodToMeal = async (dateKey, mealName, selection) => {
        // selection: { type: 'recipe'|'ingredient', item, quantity?, unit? }
        const currentDay = weeklyData[dateKey];
        if (!currentDay) return;

        const existingMenu = currentDay.meals[mealName];
        const existingId = existingMenu.rawId; // We stored this in fetchWeekData

        // Store previous state for rollback
        const previousData = JSON.parse(JSON.stringify(weeklyData));

        // Optimistic update - add item immediately to UI
        // For recipes, macros may be in totalMacronutrients, nutritionalInfo, macronutrients, or directly on item
        const getRecipeMacros = (item) => {
            const source = item.totalMacronutrients || item.nutritionalInfo || item.macronutrients || item.macros || item;
            return {
                kilocalories: source.kilocalories || source.calories || 0,
                protein: source.protein || source.proteins || 0,
                carbs: source.carbs || source.carbohydrates || 0,
                fat: source.fat || source.fats || 0
            };
        };

        const ingredientQuantityInGrams = selection.type === 'ingredient'
            ? getIngredientQuantityInGrams(selection.quantity, selection.unit, selection.item)
            : 0;

        const newItem = selection.type === 'recipe'
            ? {
                _id: selection.item._id,
                name: selection.item.name,
                mainImage: selection.item.mainImage,
                isRecipe: true,
                quantity: 1,
                unit: 'ración',
                type: 'planned-recipe',
                // Include macros for immediate display
                ...getRecipeMacros(selection.item)
            }
            : selection.type === 'stock'
                ? (() => {
                    const stock = selection.item;
                    const sourceRecipeId = stock.sourceRecipe?._id || stock.sourceRecipe || null;
                    const sourceRecipe = stock.sourceRecipe && typeof stock.sourceRecipe === 'object' ? stock.sourceRecipe : {};
                    const stockMacros = stock.macrosPerPortion || {};
                    const hasStockMacros = Object.values(stockMacros).some(v => Number(v) > 0);
                    const macros = hasStockMacros ? stockMacros : sourceRecipe.totalMacronutrients || {};
                    const portions = Number(selection.portions || 1);
                    return {
                        _id: stock._id,
                        name: stock.name,
                        mainImage: sourceRecipe.mainImage,
                        isRecipe: !!sourceRecipeId,
                        sourceRecipeId,
                        sourceRecipeName: sourceRecipe.name,
                        quantity: portions,
                        unit: stock.portionUnit || 'raciones',
                        type: 'planned-stock',
                        storage: stock.storage,
                        stockPortions: portions,
                        portionsAvailable: Math.max(0, (stock.portionsAvailable || 0) - portions),
                        kilocalories: (macros.kilocalories || 0) * portions,
                        protein: (macros.protein || 0) * portions,
                        carbs: (macros.carbs || 0) * portions,
                        fat: (macros.fat || 0) * portions,
                    };
                })()
                : {
                _id: selection.item._id,
                name: selection.item.name,
                isRecipe: false,
                quantity: selection.quantity,
                unit: selection.unit || selection.item.standardUnit || 'gramos',
                type: 'planned-ingredient',
                kilocalories: (selection.item.kilocalories || 0) * (ingredientQuantityInGrams / 100),
                protein: (selection.item.protein || 0) * (ingredientQuantityInGrams / 100),
                carbs: (selection.item.carbs || 0) * (ingredientQuantityInGrams / 100),
                fat: (selection.item.fat || 0) * (ingredientQuantityInGrams / 100),
            };

        setWeeklyData(prev => {
            const updated = JSON.parse(JSON.stringify(prev));
            const meal = updated[dateKey].meals[mealName];
            meal.items = [...(meal.items || []), newItem];
            // Update meal macros
            meal.macros.kilocalories += newItem.kilocalories || 0;
            meal.macros.protein += newItem.protein || 0;
            meal.macros.carbs += newItem.carbs || 0;
            meal.macros.fat += newItem.fat || 0;
            // Update day totals
            updated[dateKey].totals.kilocalories += newItem.kilocalories || 0;
            updated[dateKey].totals.protein += newItem.protein || 0;
            updated[dateKey].totals.carbs += newItem.carbs || 0;
            updated[dateKey].totals.fat += newItem.fat || 0;
            return updated;
        });

        try {
            let payload = {};
            if (selection.type === 'recipe') {
                payload = existingId
                    ? { addRecipe: selection.item._id }
                    : { recipes: [selection.item._id], ingredients: [] };
            } else if (selection.type === 'stock') {
                const sourceRecipeId = selection.item.sourceRecipe?._id || selection.item.sourceRecipe || null;
                const stockUse = {
                    stockItem: selection.item._id,
                    portions: Number(selection.portions || 1),
                    recipe: sourceRecipeId || undefined,
                };
                payload = existingId
                    ? {
                        ...(sourceRecipeId ? { addRecipe: sourceRecipeId } : {}),
                        addStockUse: stockUse,
                    }
                    : {
                        recipes: sourceRecipeId ? [sourceRecipeId] : [],
                        ingredients: [],
                        stockUses: [stockUse],
                    };
            } else {
                const ingPayload = {
                    ingredient: selection.item._id,
                    quantity: selection.quantity,
                    unit: selection.unit,
                    name: selection.item.name
                };
                payload = existingId
                    ? { addIngredient: ingPayload }
                    : { ingredients: [ingPayload], recipes: [] };
            }

            const savedMenu = await updateMenuBackend(existingId, payload, dateKey, mealName);
            const menuId = existingId || savedMenu?._id;

            if (selection.type === 'stock') {
                const consumeRes = await fetch(`${import.meta.env.VITE_API_URL}/food-stock/${selection.item._id}/use`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${auth.authToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        portions: Number(selection.portions || 1),
                        date: dateKey,
                        meal: mealName,
                        reason: 'planned_menu',
                        menuEntry: menuId,
                    })
                });
                if (!consumeRes.ok) throw new Error('Failed to consume stock');
            }

            // After POST (new menu created), store the rawId so subsequent
            // additions use PUT (addRecipe) instead of POST (which overwrites)
            if (!existingId && savedMenu?._id) {
                setWeeklyData(prev => {
                    const updated = JSON.parse(JSON.stringify(prev));
                    if (updated[dateKey]?.meals[mealName]) {
                        updated[dateKey].meals[mealName].rawId = savedMenu._id;
                    }
                    return updated;
                });
            }

        } catch (e) {
            console.error(e);
            // Rollback on error
            setWeeklyData(previousData);
            setError("Error al guardar el cambio.");
        }
    };

    const removeFoodFromMeal = async (dateKey, mealName, itemId) => {
        const currentDay = weeklyData[dateKey];
        if (!currentDay) return;
        const existingMenu = currentDay.meals[mealName];

        // Find item to know its type
        const targetItem = existingMenu.items?.find(i => i._id === itemId);
        if (!targetItem) return;

        // Store previous state for rollback
        const previousData = JSON.parse(JSON.stringify(weeklyData));

        // Optimistic update - remove item immediately from UI
        setWeeklyData(prev => {
            const updated = JSON.parse(JSON.stringify(prev));
            const meal = updated[dateKey].meals[mealName];
            const itemIndex = meal.items.findIndex(i => i._id === itemId);
            if (itemIndex !== -1) {
                const removedItem = meal.items[itemIndex];
                // Update meal macros
                meal.macros.kilocalories -= removedItem.kilocalories || 0;
                meal.macros.protein -= removedItem.protein || 0;
                meal.macros.carbs -= removedItem.carbs || 0;
                meal.macros.fat -= removedItem.fat || 0;
                // Update day totals
                updated[dateKey].totals.kilocalories -= removedItem.kilocalories || 0;
                updated[dateKey].totals.protein -= removedItem.protein || 0;
                updated[dateKey].totals.carbs -= removedItem.carbs || 0;
                updated[dateKey].totals.fat -= removedItem.fat || 0;
                // Remove item
                meal.items.splice(itemIndex, 1);
            }
            return updated;
        });

        try {
            if (targetItem.type === 'unplanned') {
                // Endpoint for deleting unplanned meal
                const response = await fetch(`${import.meta.env.VITE_API_URL}/unplanned-meals/${itemId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${auth.authToken}` }
                });
                if (!response.ok) {
                    throw new Error('Failed to delete unplanned meal');
                }
            } else if (targetItem.type === 'planned-stock') {
                if (!existingMenu.rawId) return;
                const body = { removeStockUse: itemId };
                if (targetItem.sourceRecipeId) body.removeRecipe = targetItem.sourceRecipeId;
                await updateMenuBackend(existingMenu.rawId, body, dateKey, mealName);

                const restoreRes = await fetch(`${import.meta.env.VITE_API_URL}/food-stock/${itemId}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${auth.authToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        portionsAvailable: (Number(targetItem.portionsAvailable) || 0) + (Number(targetItem.stockPortions) || 1),
                        status: 'available'
                    })
                });
                if (!restoreRes.ok) throw new Error('Failed to restore stock');
            } else {
                if (!existingMenu.rawId) return; // Should not happen for planned items
                let payload = {};
                if (targetItem.type === 'planned-recipe' || targetItem.isRecipe === true) { // fallback
                    payload = { removeRecipe: itemId };
                } else {
                    payload = { removeIngredient: itemId }; // legacy endpoint expects ID
                }
                await updateMenuBackend(existingMenu.rawId, payload, dateKey, mealName);
            }
            // No full refetch needed - optimistic update already applied

        } catch (e) {
            console.error(e);
            // Rollback on error
            setWeeklyData(previousData);
            setError("Error al eliminar elemento.");
        }
    };

    const makeRecipeStock = async (dateKey, mealName, recipeItem, options = {}) => {
        const currentDay = weeklyData[dateKey];
        if (!currentDay) return;
        const existingMenu = currentDay.meals[mealName];
        const existingId = existingMenu.rawId;
        const recipeId = recipeItem?._id || recipeItem?.sourceRecipeId;
        const portions = Number(options.portions || 0);

        if (!existingId || !recipeId || !Number.isFinite(portions) || portions <= 0) {
            setError("No se puede crear fondo para esta receta.");
            return;
        }

        try {
            const storage = options.storage || 'freezer';
            const createdFromPlan = `menu:${existingId}:recipe:${recipeId}:stock-prep`;
            const stockRes = await fetch(`${import.meta.env.VITE_API_URL}/food-stock`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth.authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sourceRecipe: recipeId,
                    portionsAvailable: portions,
                    portionsTotal: portions,
                    storage,
                    expiresAt: storage === 'freezer' ? undefined : options.expiresAt || undefined,
                    notes: options.notes,
                    createdFromPlan,
                })
            });
            const stockWrapper = await stockRes.json();
            if (!stockRes.ok) throw new Error(stockWrapper.message || 'Error creando fondo');

            await updateMenuBackend(existingId, {
                addStockPreparation: {
                    recipe: recipeId,
                    portions,
                    storage,
                    expiresAt: storage === 'freezer' ? undefined : options.expiresAt || undefined,
                    stockItem: stockWrapper.data?._id,
                    notes: options.notes,
                }
            }, dateKey, mealName);

            await fetchWeekData();
        } catch (e) {
            console.error(e);
            setError("Error creando fondo desde el menú.");
        }
    };

    return {
        currentWeekStart,
        weeklyData,
        weeklyTotals,
        userGoals,
        loading,
        error,
        actions: {
            nextWeek,
            prevWeek,
            goToToday,
            addFoodToMeal,
            removeFoodFromMeal,
            makeRecipeStock,
            refresh: fetchWeekData
        }
    };
};
